import { useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

interface NavigateWithQueryOptions {
  excludeParams?: string[];
  replaceParams?: Record<string, string | null>;
  addParams?: Record<string, string>;
  allowAddNewParams?: boolean; // Defaults to true
}

export const useQueryNavigation = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current query parameters as an object
  const currentParams = useMemo(() => {
    const paramMap: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      paramMap[key] = value;
    });
    return paramMap;
  }, [searchParams]);

  // Main navigation and query update function
  const navigateWithQuery = (path: string, options: NavigateWithQueryOptions = {}) => {
    const { excludeParams = [], replaceParams = {}, addParams = {}, allowAddNewParams = true } = options;

    // Work on a copy — never mutate React Router's searchParams directly,
    // otherwise React Router sees "old state === new state" and skips the re-render
    const params = new URLSearchParams(searchParams.toString());

    // Remove excluded parameters
    excludeParams.forEach((param) => {
      params.delete(param);
    });

    // Replace existing parameters or optionally add new ones
    Object.entries(replaceParams).forEach(([key, value]) => {
      if (params.has(key) || allowAddNewParams) {
        if (value === null) {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      }
    });

    // Add new parameters if allowed
    if (allowAddNewParams) {
      Object.entries(addParams).forEach(([key, value]) => {
        params.set(key, value);
      });
    }

    navigate(
      { pathname: path, search: params.toString() },
      { replace: path === pathname },
    );
  };

  // Return the navigation function and current parameters
  return { currentParams, navigateWithQuery, setSearchParams, pathname };
};
